import type { CaptionSegment } from "./transcript";
import type { FormattedTranscript } from "./domain";

/** Remove transcription noise: zero-width chars, hangul filler, excess spaces. */
export function cleanText(input: string): string {
  return input
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\((?:\w+\s*\/+\s*\w+)\)/g, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
}

export function formatTimestamp(totalSeconds: number): string {
  const secs = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Whitespace-delimited word count, used for the text-input word limits. */
export function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

/**
 * Approximate chars for a word budget. Used to translate the text-mode
 * per-tier word budget into the char window `selectTranscriptWindow` expects.
 * 6 chars/word keeps the paid budget (10k words) in step with the default
 * `MAX_TRANSCRIPT_CHARS` (60k chars).
 */
export const TEXT_MODE_CHARS_PER_WORD = 6;

export function wordBudgetToChars(words: number): number {
  return Math.max(0, Math.floor(words * TEXT_MODE_CHARS_PER_WORD));
}

/**
 * Drop segments whose text nearly matches the previous one within a short
 * window — a common artifact of rolling/auto-generated captions.
 */
export function dedupeSegments(segments: readonly CaptionSegment[]): CaptionSegment[] {
  const out: CaptionSegment[] = [];
  for (const seg of segments) {
    const prev = out[out.length - 1];
    if (prev && seg.start - prev.start < 1.5) {
      const overlap = longestCommonPrefixLength(prev.text, seg.text);
      if (overlap / Math.max(seg.text.length, 1) > 0.6) {
        continue;
      }
    }
    out.push(seg);
  }
  return out;
}

function longestCommonPrefixLength(a: string, b: string): number {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i++;
  return i;
}

/**
 * Build a properly-formatted transcript: paragraphs grouped by ~45s of spoken
 * content with a `[mm:ss]` marker per group, blank-line separated.
 * Exported separately so it can be unit tested.
 */
export function buildTranscriptText(segments: readonly CaptionSegment[]): string {
  const cleaned = dedupeSegments(segments)
    .map((s) => ({ text: cleanText(s.text), start: s.start, duration: s.duration }))
    .filter((s) => s.text.length > 0);

  if (!cleaned.length) return "";

  const chunks: Array<{ start: number; text: string }> = [];
  let current: { start: number; text: string } | null = null;

  for (const seg of cleaned) {
    if (!current) {
      current = { start: seg.start, text: seg.text };
      continue;
    }
    if (seg.start - current.start >= 45 && !current.text.endsWith(" ") && current.text.length > 0) {
      chunks.push(current);
      current = { start: seg.start, text: seg.text };
    } else {
      current.text += ` ${seg.text}`;
    }
    if (current.text.length > 2600) {
      chunks.push(current);
      current = null;
    }
  }
  if (current) chunks.push(current);

  return chunks.map((c) => `[${formatTimestamp(c.start)}] ${c.text}`).join("\n\n");
}

/** Sample long transcripts instead of truncating mid-sentence. */
export function selectTranscriptWindow(text: string, maxChars: number): { text: string; truncated: boolean } {
  if (text.length <= maxChars) return { text, truncated: false };

  const headEnd = Math.floor(maxChars * 0.7);
  const tailLen = Math.floor(maxChars * 0.2);
  const head = text.slice(0, headEnd);
  const tail = text.slice(-tailLen);
  const middleNote = `\n\n[... ${Math.floor((text.length - maxChars) / 1000)} thousand characters omitted for brevity ...]\n\n`;
  return { text: `${head}${middleNote}${tail}`, truncated: true };
}

export interface FormatTranscriptInput {
  segments: readonly CaptionSegment[];
  language: string | null;
  durationSeconds: number | null;
  maxChars: number;
}

export function formatTranscript(input: FormatTranscriptInput): FormattedTranscript {
  const raw = buildTranscriptText(input.segments);
  const { text, truncated } = selectTranscriptWindow(raw, input.maxChars);
  return {
    text,
    language: input.language,
    segmentCount: input.segments.length,
    durationSeconds: input.durationSeconds,
    truncated,
  };
}

export interface FormatStoredTextInput {
  /** Cleaned, un-windowed source text (stored transcript or text-mode input). */
  text: string;
  language: string | null;
  segmentCount: number;
  durationSeconds: number | null;
  maxChars: number;
}

/**
 * Window already-extracted source text for the model. Used for user-supplied
 * transcripts and text-input quizzes, where there are no timed segments to
 * rebuild paragraphs from.
 */
export function formatStoredText(input: FormatStoredTextInput): FormattedTranscript {
  const cleaned = cleanText(input.text);
  const { text, truncated } = selectTranscriptWindow(cleaned, input.maxChars);
  return {
    text,
    language: input.language,
    segmentCount: input.segmentCount,
    durationSeconds: input.durationSeconds,
    truncated,
  };
}